"use client"
import { Accordion, Stack, Text, VStack } from "@chakra-ui/react"
import { type FC } from "react"

import MarkdownParser from "@/components/MarkdownParser"
import { type FaqBlock as FaqBlockType } from "@/types/database"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface FaqBlockProps extends ContentBlockTitleNumberProps {
  block: FaqBlockType
}

interface FaqItem {
  question: string
  answer: string
}

const getStringValue = (value: unknown) => {
  if (typeof value === "string" || typeof value === "number") {
    const text = String(value).trim()

    return text.length > 0 ? text : undefined
  }

  return undefined
}

const getNestedFaqSource = (record: Record<string, unknown>) =>
  record.item ??
  record.faq ??
  record.faq_id ??
  record.faqs_id ??
  record.faq_item ??
  record.directus_faqs_id

const getFaqItem = (value: unknown): FaqItem | null => {
  if (!value || typeof value !== "object") {
    return null
  }

  const record = value as Record<string, unknown>
  const nestedFaqSource = getNestedFaqSource(record)

  if (nestedFaqSource && nestedFaqSource !== value) {
    return getFaqItem(nestedFaqSource)
  }

  const question =
    getStringValue(record.question) ??
    getStringValue(record.faq_question) ??
    getStringValue(record.title) ??
    getStringValue(record.heading) ??
    getStringValue(record.label)
  const answer =
    getStringValue(record.answer) ??
    getStringValue(record.anwser) ??
    getStringValue(record.faq_answer) ??
    getStringValue(record.content) ??
    getStringValue(record.description) ??
    getStringValue(record.text)

  if (!question && !answer) {
    return null
  }

  return {
    answer: answer ?? "",
    question: question ?? "Question",
  }
}

const getFaqItems = (value: unknown): FaqItem[] => {
  if (!value) {
    return []
  }

  if (typeof value === "string") {
    try {
      return getFaqItems(JSON.parse(value) as unknown)
    } catch {
      return []
    }
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      const faq = getFaqItem(item)

      return faq ? [faq] : []
    })
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>
    const faqSource = record.data ?? record.faqs ?? record.items ?? record.value

    if (faqSource) {
      return getFaqItems(faqSource)
    }

    const faq = getFaqItem(record)

    return faq ? [faq] : []
  }

  return []
}

const getFaqValue = (faq: FaqItem, index: number) =>
  `faq-${index}-${faq.question}`

const FaqBlock: FC<FaqBlockProps> = ({ block, titleNumber }) => {
  const { alignment, darker_bg, faqs, title, two_columns_faq } = block
  const trimmedTitle = title?.trim()
  const hasTitle = Boolean(trimmedTitle)
  const faqsArray = getFaqItems(faqs)

  return (
    <Stack
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="faq-block internal-padding"
      direction={{ base: "column", lg: two_columns_faq ? "row" : "column" }}
      gap={{ base: 8, lg: two_columns_faq ? 6 : 8 }}
      position="relative"
    >
      {hasTitle && (
        <VStack
          alignItems="flex-start"
          w={{
            base: "100%",
            lg: two_columns_faq ? "30%" : "100%",
          }}
          {...getStickyContentBlockLeftProps(Boolean(two_columns_faq))}
        >
          <ContentBlockTitle
            size="lg"
            textAlign={alignment || "left"}
            title={trimmedTitle}
            titleNumber={titleNumber}
          />
        </VStack>
      )}
      <Accordion.Root
        collapsible
        display="flex"
        flexDirection="column"
        gap={4}
        multiple
        pl={!hasTitle && two_columns_faq ? { base: 0, lg: 12 } : 0}
        variant="plain"
        w={{
          base: "100%",
          lg: two_columns_faq ? (hasTitle ? "70%" : "100%") : "100%",
        }}
      >
        {faqsArray.map((faq: FaqItem, index: number) => (
          <Accordion.Item
            borderColor="border"
            borderRadius="lg"
            borderWidth="1px"
            key={getFaqValue(faq, index)}
            overflow="hidden"
            value={getFaqValue(faq, index)}
          >
            <Accordion.ItemTrigger
              _hover={{ bgColor: "bg.subtle" }}
              justifyContent="space-between"
              px={4}
              py={3}
              width="full"
            >
              <Text
                flex="1"
                fontFamily="header"
                fontSize={{ base: "lg" }}
                fontWeight="medium"
                textAlign="left"
              >
                {faq.question}
              </Text>
              <Accordion.ItemIndicator color="fg.muted" />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent borderTopColor="border" borderTopWidth="1px">
              <Accordion.ItemBody
                borderTopColor="border"
                color="fg.read"
                p={4}
                pt={4}
              >
                <MarkdownParser
                  color="fg.read"
                  fontSize={{ base: "md" }}
                  markdown={faq.answer}
                  pProps={{ color: "fg.read", marginBottom: 0 }}
                />
              </Accordion.ItemBody>
            </Accordion.ItemContent>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </Stack>
  )
}

export default FaqBlock
